import { Button, FormControl, FormHelperText, Grid, Input, InputLabel, MenuItem, Select } from '@mui/material';
import Modal from 'components/Modal/Modal';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from 'utils/price-format';

type DiscountCodeModalProps = {
  name: string;
  isOpen: boolean;
  formErrors: {
    code?: string[];
    discount_type?: string[];
    discount_amount?: string[];
  };
  discountType: string;
  discountAmount: string;
  maxValue: number;
  earningNote: string;
  onClose: () => void;
  onDiscountAmountChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDiscountTypeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => Promise<void>;
};

export const DiscountCodeModal = ({
  earningNote,
  maxValue,
  discountType,
  discountAmount,
  isOpen,
  formErrors,
  name,
  onClose,
  onNameChange,
  onDiscountTypeChange,
  onDiscountAmountChange,
  onSubmit,
}: DiscountCodeModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="components.discount-code-modal.modal-title"
      className="vouchers-modal"
      titleUnderline={false}
      closeButtonVisible={false}
    >
      {/* Promo code name */}
      <Grid container>
        <FormControl fullWidth>
          <InputLabel htmlFor="discount-name">{t('components.discount-code-modal.name')}</InputLabel>
          <Input fullWidth id="discount-name" type="text" value={name} onChange={onNameChange} error={!!formErrors?.code?.length} />
          {!!formErrors?.code?.length && (
            <FormHelperText error={true} className="voucher-name-error">
              {formErrors?.code?.join(', ')}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Discount */}
      <Grid container className="pt-1">
        {/* Discount type */}
        <Grid xs={12}>
          <FormControl fullWidth>
            <InputLabel id="discount-type-label">{t('components.discount-code-modal.discount-type-label')}</InputLabel>
            <Select
              variant="standard"
              labelId="discount-type-label"
              value={discountType}
              onChange={onDiscountTypeChange}
              error={!!formErrors?.discount_type?.length}
            >
              <MenuItem value="percentage">{t('components.discount-code-modal.discount-type-percentage')}</MenuItem>
              <MenuItem value="sum">
                {t('components.discount-code-modal.discount-type-sum', {
                  currency: getCurrencySymbol(),
                })}
              </MenuItem>
            </Select>
            {!!formErrors?.discount_type?.length && (
              <FormHelperText error={true} className="discount-type-error">
                {formErrors?.discount_type?.join(', ')}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Discount amount */}
        <Grid container className="pt-1">
          {!!discountType && (
            <Grid xs={12}>
              <FormControl fullWidth>
                <InputLabel htmlFor="discount-amount">{t('components.discount-code-modal.discount-amount')}</InputLabel>
                <Input
                  fullWidth
                  id="discount-amount"
                  value={discountAmount || ''}
                  onChange={onDiscountAmountChange}
                  type="number"
                  inputProps={{ max: maxValue }}
                  error={!!formErrors?.discount_amount?.length}
                  placeholder={discountType === 'sum' ? '' : '1-99'}
                />
                {!!formErrors?.discount_amount?.length && (
                  <FormHelperText error={true} className="discount-amount-error">
                    {formErrors?.discount_amount?.join(', ')}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* Note */}
      <div className="voucher-modal-note">{t('components.discount-code-modal.discount-note')}</div>

      {/* Actions */}
      <div className="voucher-modal-actions">
        {/* cancel */}
        <Button variant="outlined" color="primary" onClick={onClose} className="voucher-modal-cancel-btn">
          {t('components.discount-code-modal.cancel')}
        </Button>

        {/* save */}
        <Button variant="contained" color="primary" onClick={onSubmit}>
          {t('components.discount-code-modal.save')}
        </Button>

        <div className="earning-note" dangerouslySetInnerHTML={{ __html: earningNote }} />
      </div>
    </Modal>
  );
};
